<div class="ap-card"
     [class.ap-card-committed]="finalState?.kind === 'committed'"
     [class.ap-card-cancelled]="finalState?.kind === 'cancelled'">

    <!-- =====================================================================
         CANCELLED (rejected)
         Figma 1575-25025. Compact: just the action title + a red "Rejected"
         badge, then a single footer row "Rejected — no changes committed".
         No "Try again" affordance (designer-confirmed cut).
         ===================================================================== -->
    <ng-container *ngIf="finalState?.kind === 'cancelled'">
        <div class="ap-card-header">
            <div class="ap-card-header-text">
                <h4 class="ap-card-title">{{ headerTitle }}</h4>
            </div>
            <span class="ap-card-state-badge tone-rejected">Rejected</span>
        </div>
        <div class="ap-card-footer ap-card-footer-rejected">
            <span class="ap-card-footer-label">Rejected — no changes committed</span>
        </div>
    </ng-container>

    <!-- =====================================================================
         COMMITTED (approved)
         Collapsed to the footer only after the action commits — the preview
         body (template / recipients / impact summary) hides so the user
         can't re-confirm or re-edit a write that already happened. Just a
         green success line + "View created" deeplink remain.
         ===================================================================== -->
    <ng-container *ngIf="finalState?.kind === 'committed'">
        <!-- Shared V2 alert-block (per Catherine 2026-07-11) instead of the
             bespoke green footer row. -->
        <app-alert-block type="success" class="ap-card-committed-alert">
            {{ completedLabel }}
            <a
                *ngIf="destinationRoute"
                class="ap-card-view-created"
                [routerLink]="destinationRoute">
                {{ destinationLabel }}
                <i class="ph-bold ph-arrow-up-right icon-text icon-14" aria-hidden="true"></i>
            </a>
        </app-alert-block>
    </ng-container>

    <!-- =====================================================================
         PENDING (interactive)
         ===================================================================== -->
    <ng-container *ngIf="finalState === null">
      <div class="ap-card-body" *ngIf="hasBodyContent">
        <!-- Hoisted context row (A2): affected record left, deep-link right. -->
        <div class="ap-context-row" *ngIf="contextRowVisible">
            <span class="ap-context-record" *ngIf="contextRecord">{{ contextRecord }}</span>
            <a
                *ngIf="contextLink as link"
                class="ap-context-link"
                [routerLink]="link.route">
                {{ link.label }}
                <i class="ph-bold ph-arrow-square-out ap-context-link-icon" aria-hidden="true"></i>
            </a>
        </div>

        <ng-container *ngFor="let section of bodySections; let i = index; trackBy: trackBySectionIndex">
            <div class="ap-section" [attr.data-kind]="section.kind">
                <ng-container [ngSwitch]="section.kind">
                    <app-ap-header-section
                        *ngSwitchCase="'header'"
                        [section]="$any(section)"
                        [scaleLine]="scaleLine">
                    </app-ap-header-section>

                    <app-ap-status-picker-section
                        *ngSwitchCase="'status_picker'"
                        [section]="$any(section)"
                        [pendingEdit]="pendingEdits.status_picker ?? null"
                        (edit)="onStatusEdit($event)">
                    </app-ap-status-picker-section>

                    <app-ap-email-template-section
                        *ngSwitchCase="'email_template'"
                        [section]="$any(section)"
                        [pendingEdit]="pendingEdits.email_template ?? null"
                        (edit)="onEmailEdit($event)">
                    </app-ap-email-template-section>

                    <app-ap-toggle-section
                        *ngSwitchCase="'toggle'"
                        [section]="$any(section)"
                        [pendingValue]="toggleValue($any(section).key)"
                        [disabled]="finalState !== null || submitting"
                        (edit)="onToggleEdit($event)">
                    </app-ap-toggle-section>

                    <app-ap-recipients-section
                        *ngSwitchCase="'recipients'"
                        [section]="$any(section)"
                        [pendingEdit]="pendingEdits.recipients ?? null"
                        (edit)="onRecipientsEdit($event)"
                        (listAction)="onRecipientsListAction($event)">
                    </app-ap-recipients-section>

                    <app-ap-recipients-people-section
                        *ngSwitchCase="'recipients_people'"
                        [section]="$any(section)"
                        [pendingEdit]="pendingEdits.recipients ?? null"
                        (edit)="onRecipientsEdit($event)">
                    </app-ap-recipients-people-section>

                    <app-ap-impact-summary-section
                        *ngSwitchCase="'impact_summary'"
                        [section]="$any(section)"
                        [primaryOverride]="impactSummaryPrimaryOverride">
                    </app-ap-impact-summary-section>

                    <app-ap-key-value-section
                        *ngSwitchCase="'key_value'"
                        [section]="$any(section)">
                    </app-ap-key-value-section>

                    <app-ap-info-block-section
                        *ngSwitchCase="'info_block'"
                        [section]="$any(section)">
                    </app-ap-info-block-section>

                    <ng-container *ngSwitchCase="'deep_link'">
                        <!-- Skipped when hoisted into the context row (A2). -->
                        <app-ap-deep-link-section
                            *ngIf="!contextRowVisible"
                            [section]="$any(section)">
                        </app-ap-deep-link-section>
                    </ng-container>

                    <app-ap-outcome-section
                        *ngSwitchCase="'outcome'"
                        [section]="$any(section)"
                        [confidence]="confidence"
                        [effectOverride]="outcomeEffectOverride">
                    </app-ap-outcome-section>

                    <app-ap-why-section
                        *ngSwitchCase="'why'"
                        [section]="$any(section)">
                    </app-ap-why-section>

                    <app-ap-guardrail-chips-section
                        *ngSwitchCase="'guardrail_chips'"
                        [section]="$any(section)">
                    </app-ap-guardrail-chips-section>

                    <app-ap-choice-section
                        *ngSwitchCase="'choice'"
                        [section]="$any(section)"
                        [pendingEdit]="pendingEdits.choice ?? null"
                        [confidence]="confidence"
                        (edit)="onChoiceEdit($event)">
                    </app-ap-choice-section>

                    <div *ngSwitchDefault class="ap-section-placeholder">
                        <span class="ap-section-placeholder-kind">{{ section.kind }}</span>
                        <span *ngIf="!isKnownKind(section)" class="ap-section-placeholder-note">
                            Unknown section kind — UI not yet built. Backend may have shipped a
                            newer section type than this client knows about.
                        </span>
                    </div>
                </ng-container>
            </div>
        </ng-container>
      </div>

        <!-- Say why the primary control is dead BEFORE the user clicks it; a tooltip
             alone is not discoverable. -->
        <div class="ap-viewer-blocked" *ngIf="viewerCannotPerform">
            <i class="ph-bold ph-info icon-16" aria-hidden="true"></i>
            <span>
                {{ viewerCapability?.reason }}
                <ng-container *ngIf="viewerCannotPerformDetail"> {{ viewerCannotPerformDetail }}</ng-container>
            </span>
        </div>

        <div class="ap-card-actions"
             *ngIf="payload?.preview?.actions as actions">
            <div class="ap-footer-actions">
                <button
                    type="button"
                    mat-flat-button
                    [disableRipple]="true"
                    color="primary"
                    class="ap-primary-button"
                    [showSpinner]="submitting"
                    [spinnerSize]="16"
                    [disabled]="submitting || snoozing || isPrimaryDisabled"
                    [title]="isPrimaryDisabled ? primaryDisabledReason : null"
                    (click)="onConfirm()">
                    <i *ngIf="!submitting && actions.primary.icon === 'check'"
                       class="ph-bold ph-check icon-white icon-16"
                       aria-hidden="true"></i>
                    {{ submitting ? 'Saving…' : primaryButtonLabel }}
                </button>
                <button
                    type="button"
                    mat-flat-button
                    [disableRipple]="true"
                    color="accent"
                    class="ap-destructive-button"
                    [disabled]="submitting || snoozing"
                    (click)="onCancel()">
                    {{ actions.destructive.label }}
                </button>
            </div>
            <button
                type="button"
                class="ap-snooze-button"
                [disabled]="submitting || snoozing"
                (click)="onSnooze()">
                <i class="ph-bold ph-clock-clockwise icon-16" aria-hidden="true"></i>
                {{ snoozing ? 'Snoozing…' : 'Snooze until tomorrow' }}
            </button>
        </div>
    </ng-container>
</div>
